import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, LogOut, User, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { clearAuthTokens, getPortalType, getUserData, clearAllSessions, getAuthToken } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import axios from 'axios';

export default function GuardianSidebar({ activeSection, setActiveSection, onNavigate = () => {}, showCloseButton = false, onClose = () => {}, onLogout = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userProfile, setUserProfile] = useState(null);

  // Auto-set active section based on URL
  useEffect(() => {
    if (location.pathname.includes('/community')) {
      setActiveSection('community');
    }
  }, [location.pathname, setActiveSection]);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = () => {
      try {
        // Guardians use 'guardian' portal for auth tokens
        const userData = getUserData('guardian');
        if (userData) {
          // Get profile photo from backend
          let photoUrl = null;
          const photoField = userData.profile_photo || userData.profile_pic_url || userData.photo || userData.avatar;

          if (photoField) {
            if (photoField.startsWith('http://') || photoField.startsWith('https://')) {
              photoUrl = photoField;
            } else {
              const cleanPath = photoField.startsWith('/') ? photoField.slice(1) : photoField;
              photoUrl = apiUrl(cleanPath);
            }
          }

          const gradeName = userData.student?.grade?.grade_name || userData.grade?.grade_name || null;
          const gradeNumber = userData.student?.grade?.grade_number || userData.grade?.grade_number || null;

          setUserProfile({
            name: userData.full_name || userData.username || 'Guardian',
            photo: photoUrl,
            username: userData.username?.includes('__') ? userData.username.split('__')[1] : (userData.username || ''),
            grade: gradeName,
            gradeNumber: gradeNumber,
          });
        }
      } catch (error) {
        console.error('Error loading guardian profile:', error);
        // Set default profile if loading fails
        setUserProfile({
          name: 'Guardian',
          photo: null,
          username: '',
        });
      }
    };

    loadUserProfile();
  }, []);

  const handleItemClick = (itemId) => {
    setActiveSection(itemId);
    if (itemId === 'community') {
      navigate('/guardian/community');
    }
    try { onNavigate(); } catch {}
  };

  return (
    <div className="min-w-fit w-full max-w-[3.5rem] sm:max-w-xs sm:max-w-sm h-screen bg-[hsl(var(--sidebar-bg))] sm:bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--card-border))] flex flex-col transition-all duration-300 pb-safe">
      {/* Logo + Brand Name */}
      <div className="p-4 sm:p-6 pb-6 border-b border-[hsl(var(--card-border))] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt="Budlee AI Logo"
            className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0"
          />
          <div className="flex-1 flex flex-col items-start gap-1">
            <img 
              src="/logo_text.png" 
              alt="Budlee AI" 
              className="h-5 sm:h-6 object-contain w-auto" 
            />
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Guardian Portal</p>
          </div>
        </div>
        {/* Close Button for Mobile */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className="p-1 rounded-full bg-white/90 hover:bg-white shadow z-50 lg:hidden"
            title="Close sidebar"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
        )}
      </div>

      {/* Section 2: Menu Items - Only Community */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        <div className="space-y-2">
          <button
            onClick={() => handleItemClick('community')}
          className={cn(
            'w-full flex items-center gap-4 px-4 py-3.5 rounded-xl font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
            activeSection === 'community'
              ? 'bg-[hsl(var(--sidebar-hover))] text-[hsl(var(--foreground))] shadow-md'
              : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sidebar-hover))]/50 hover:text-[hsl(var(--foreground))]'
          )}
        >
          {activeSection === 'community' && (
            <div className="absolute inset-0 rounded-xl p-[2px] bg-gradient-to-r from-[hsl(var(--green-bright))] via-[hsl(var(--teal-vivid))] to-[hsl(var(--green-bright))] animate-pulse">
              <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-xl" />
            </div>
          )}
          <div className="relative z-10 flex items-center gap-4 w-full">
            <Users className="w-5 h-5 flex-shrink-0 text-[hsl(var(--green-bright))]" />
            <span className="flex-1 text-left">COMMUNITY</span>
          </div>
        </button>
        <button
          onClick={() => {
            setActiveSection('about');
            navigate('/guardian/about');
          }}
          className={cn(
            'w-full flex items-center gap-4 px-4 py-3.5 rounded-xl font-medium text-sm uppercase tracking-wide transition-all duration-300 relative overflow-hidden',
            activeSection === 'about'
              ? 'bg-[hsl(var(--sidebar-hover))] text-[hsl(var(--foreground))] shadow-md'
              : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sidebar-hover))]/50 hover:text-[hsl(var(--foreground))]'
          )}
        >
          {activeSection === 'about' && (
            <div className="absolute inset-0 rounded-xl p-[2px] bg-gradient-to-r from-[hsl(var(--green-bright))] via-[hsl(var(--teal-vivid))] to-[hsl(var(--green-bright))] animate-pulse">
              <div className="w-full h-full bg-[hsl(var(--sidebar-hover))] rounded-xl" />
            </div>
          )}
          <div className="relative z-10 flex items-center gap-4 w-full">
            <Info className="w-5 h-5 flex-shrink-0 text-[hsl(var(--green-bright))]" />
            <span className="flex-1 text-left">ABOUT</span>
          </div>
        </button>
      </div>
      </nav>

      {/* Section 3: User Profile & Logout */}
      <div className="p-3 border-t border-[hsl(var(--card-border))]">
        {/* User Profile */}
        {userProfile ? (
          <div className="flex items-center justify-between gap-3 px-2">
            <div className="flex items-center gap-3 min-w-0">
              {userProfile.photo ? (
                <img
                  src={userProfile.photo}
                  alt={userProfile.name}
                  className="w-10 h-10 rounded-full object-cover border border-[hsl(var(--card-border))]"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate capitalize">{userProfile.username}</p>
                <p className="text-[11px] text-muted-foreground truncate uppercase tracking-widest font-semibold">
                  {userProfile.grade ? userProfile.grade : 'Guardian'}
                </p>
              </div>
            </div>

            {/* Logout Icon Button */}
            <button
              onClick={onLogout}
              className={cn(
                'p-2 rounded-lg transition-all duration-300',
                'text-red-400 hover:bg-red-500/10 hover:text-red-300'
              )}
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex justify-end px-2">
            <button
              onClick={onLogout}
              className={cn(
                'p-2 rounded-lg transition-all duration-300',
                'text-red-400 hover:bg-red-500/10 hover:text-red-300'
              )}
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
